import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { ThunkApiConfig } from 'core/store';
import getConfiguration, { errorHandler } from 'core/store/config';
import {
  ChiaveMisura,
  Configuration,
  ControllerProjectApi,
  DashboardBookingListDto,
  DashboardControllerApi,
  DashboardObiettiviPlDto,
  DashboardProjectFilterDto,
  DashboardProjectFilterInputDto,
  DashboardProjectListDto,
  DashboardUserListDto,
  GetObiettiviPLRequest,
  GetProjectListRequest,
  GetUnderBookingRequest,
  GetUserListRequest,
  RptSalPlannedFilterDto,
  RptSalPlannedSearchResponseDto,
  SearchSalMeseAnnoRequest,
  SearchStoricoAnnoRequest,
  UserListFilterDto,
  VwStoricoAnnoPagedDto,
  VwStoricoAnnoSearchDto,
} from '@api/client';

// Filtri del tab Storico (dataConfronto viene inviata al BE come dataSnapshot)
export type StoricoFiltersDto = Pick<VwStoricoAnnoSearchDto, 'fkChiaveMisura'> & { dataConfronto?: string };

enum DashboardActions {
  getDashboardProjects = '[Dashboard] Get Dashboard Projects',
  getDashboardProjectsCurrentYear = '[Dashboard] Get Dashboard Projects Current Year',
  getDashboardUsers = '[Dashboard] Get Dashboard Users',
  getDashboardProjectFilters = '[Dashboard] Get Dashboard Project Filters',
  getUnderbookingUsers = '[Dashboard] Get Underbooking Users',
  getOverbookingUsers = '[Dashboard] Get Overbooking Users',
  saveDashboardProjectFilters = '[Dashboard] Save Dashboard Project Filters',
  saveDashboardPeopleFilters = '[Dashboard] Save Dashboard People Filters',
  getPlGoals = '[Dashboard] Get PL Goals',
  getSALPlanning = '[Dashboard] Get SAL Planning',
  saveDashboardSalFilters = '[Dashboard] Save Dashboard SAL Filters',
  getStoricoAnno = '[Dashboard] Get Storico Anno',
  getChiaviMisura = '[Dashboard] Get Chiavi Misura',
  saveDashboardStoricoFilters = '[Dashboard] Save Dashboard Storico Filters',
}

export const getDashboardProjects = createAsyncThunk<
  DashboardProjectListDto | undefined,
  GetProjectListRequest,
  ThunkApiConfig
>(DashboardActions.getDashboardProjects, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).getProjectCurrentYearList(
      payload
    );
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getDashboardProjectsCurrentYear = createAsyncThunk<
  DashboardProjectListDto | undefined,
  GetProjectListRequest,
  ThunkApiConfig
>(DashboardActions.getDashboardProjectsCurrentYear, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).getProjectCurrentYearList(
      payload
    );
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getDashboardUsers = createAsyncThunk<DashboardUserListDto | undefined, GetUserListRequest, ThunkApiConfig>(
  DashboardActions.getDashboardUsers,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).getUserList(payload);
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const getDashboardProjectFilters = createAsyncThunk<DashboardProjectFilterDto | undefined, void, ThunkApiConfig>(
  DashboardActions.getDashboardProjectFilters,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await new DashboardControllerApi(
        getConfiguration(Configuration, extra, getState)
      ).getDashboardProjectFilter();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const getUnderbookingUsers = createAsyncThunk<
  DashboardBookingListDto | undefined,
  GetUnderBookingRequest,
  ThunkApiConfig
>(DashboardActions.getUnderbookingUsers, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).getUnderBooking(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getOverbookingUsers = createAsyncThunk<
  DashboardBookingListDto | undefined,
  GetUnderBookingRequest,
  ThunkApiConfig
>(DashboardActions.getOverbookingUsers, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).getOverBooking(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getPlGoals = createAsyncThunk<DashboardObiettiviPlDto | undefined, GetObiettiviPLRequest, ThunkApiConfig>(
  DashboardActions.getPlGoals,
  async (payload, { dispatch, extra, getState }) => {
    try {
      return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).getObiettiviPL(payload);
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const getSALPlanning = createAsyncThunk<
  RptSalPlannedSearchResponseDto | undefined,
  SearchSalMeseAnnoRequest,
  ThunkApiConfig
>(DashboardActions.getSALPlanning, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new DashboardControllerApi(getConfiguration(Configuration, extra, getState)).searchSalMeseAnno(
      payload
    );
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getStoricoAnno = createAsyncThunk<
  VwStoricoAnnoPagedDto | undefined,
  SearchStoricoAnnoRequest,
  ThunkApiConfig
>(DashboardActions.getStoricoAnno, async (payload, { dispatch, extra, getState }) => {
  try {
    return await new ControllerProjectApi(getConfiguration(Configuration, extra, getState)).searchStoricoAnno(payload);
  } catch (e) {
    errorHandler(e, dispatch, getState);
  }
});

export const getChiaviMisura = createAsyncThunk<ChiaveMisura[] | undefined, void, ThunkApiConfig>(
  DashboardActions.getChiaviMisura,
  async (_, { dispatch, extra, getState }) => {
    try {
      return await new ControllerProjectApi(getConfiguration(Configuration, extra, getState)).getChiaviMisura();
    } catch (e) {
      errorHandler(e, dispatch, getState);
    }
  }
);

export const saveDashboardProjectFilters = createAction<DashboardProjectFilterInputDto>(
  DashboardActions.saveDashboardProjectFilters
);

export const saveDashboardPeopleFilters = createAction<UserListFilterDto>(DashboardActions.saveDashboardPeopleFilters);

export const saveDashboardSalFilters = createAction<RptSalPlannedFilterDto>(DashboardActions.saveDashboardSalFilters);

export const saveDashboardStoricoFilters = createAction<StoricoFiltersDto>(
  DashboardActions.saveDashboardStoricoFilters
);
